import {
  AiPromptCreateRequest,
  AiPromptResponse,
  AiPromptSaveRequest,
  AiPromptSummaryResponse,
  AiPromptWriteResult,
} from '@ydistri/api-sdk';
import { apiSlice, ErrorLike, errorTransform } from '../../../apis/api';
import { getSelectedProjectShortName } from '../../../apis/apiHeader';
import { getTags } from '../../../apis/apiLib';
import { signalrClient } from '../../../signalr/client';
import { SignalAiPromptsChanged } from '../../../signalr/signalrInterfaces';
import { AiPromptsCollection } from '../../../swagger/collections';

const { TAGS, TAGS_ARRAY } = getTags('aiPrompts');

export const apiAiPrompts = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- the collection takes no arguments
    getAiPrompts: builder.query<AiPromptSummaryResponse[], void>({
      queryFn: async () => {
        try {
          const response = await AiPromptsCollection.getAiPrompts();
          return { data: response.data.data };
        } catch (error) {
          // A plain copy: the raw axios error is not serializable and would not be stored whole.
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      providesTags: [TAGS.aiPromptsList],
      async onCacheEntryAdded(arg, api) {
        // The entry's own project: it outlives a project switch by keepUnusedDataFor.
        const project = getSelectedProjectShortName()?.toLowerCase();

        // Registered before the first read answers, so a change during it, or after a failed one, reads again.
        const listener = (signal: SignalAiPromptsChanged) => {
          if (signal.projectSlug.toLowerCase() !== project) return;
          api.dispatch(apiAiPrompts.util.invalidateTags([TAGS.aiPromptsList, TAGS.aiPrompt]));
        };

        signalrClient.on('AiPromptsChanged', listener);
        await api.cacheEntryRemoved;
        signalrClient.off('AiPromptsChanged', listener);
      },
    }),
    getAiPrompt: builder.query<AiPromptResponse, number>({
      queryFn: async aiPromptId => {
        try {
          const response = await AiPromptsCollection.getAiPrompt(aiPromptId);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      providesTags: (result, error, aiPromptId) => [{ type: TAGS.aiPrompt, id: aiPromptId }],
    }),
    getAiPromptVersions: builder.query<AiPromptResponse, number>({
      queryFn: async aiPromptId => {
        try {
          const response = await AiPromptsCollection.getAiPromptVersions(aiPromptId);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      providesTags: (result, error, aiPromptId) => [{ type: TAGS.aiPrompt, id: aiPromptId }],
    }),
    createAiPrompt: builder.mutation<AiPromptWriteResult, AiPromptCreateRequest>({
      queryFn: async payload => {
        try {
          const response = await AiPromptsCollection.createAiPrompt(payload);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: [TAGS.aiPromptsList],
    }),
    saveAiPrompt: builder.mutation<
      AiPromptWriteResult,
      { aiPromptId: number; payload: AiPromptSaveRequest }
    >({
      queryFn: async ({ aiPromptId, payload }) => {
        try {
          const response = await AiPromptsCollection.saveAiPrompt(aiPromptId, payload);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: (result, error, arg) => [
        TAGS.aiPromptsList,
        { type: TAGS.aiPrompt, id: arg.aiPromptId },
      ],
    }),
    deleteAiPrompt: builder.mutation<void, number>({
      queryFn: async aiPromptId => {
        try {
          await AiPromptsCollection.deleteAiPrompt(aiPromptId);
          return { data: undefined };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      invalidatesTags: (result, error, aiPromptId) => [
        TAGS.aiPromptsList,
        { type: TAGS.aiPrompt, id: aiPromptId },
      ],
    }),
  }),
});

export const {
  useGetAiPromptsQuery,
  useGetAiPromptQuery,
  useGetAiPromptVersionsQuery,
  useCreateAiPromptMutation,
  useSaveAiPromptMutation,
  useDeleteAiPromptMutation,
} = apiAiPrompts;
